import { Component, OnInit, Input, Output, EventEmitter, ElementRef, ViewChild } from '@angular/core';
import { FileUploader } from 'ng2-file-upload';

import { DocumentService } from '../../../services/documents/document.service';
import { DocumentStoreService } from '../../../services/documents/document-store.service';
import { MatterStoreService } from '../../../services/matters/matter-store.service';
import { ModalService } from '../../../services/common/modal.service';
import { MatterService } from '../../../services/matters/matter.service';
import { NotyService } from '../../../services/common/noty.service';
import { TagService } from '../../../services/common/tag.service';

declare var window: any;

@Component({
  selector: 'app-document-show',
  templateUrl: './document-show.component.html',
  styleUrls: ['./document-show.component.css'],
})
export class DocumentShowComponent implements OnInit {
  /**
   * Doc object with document info from parent component
   */
  @Input() doc: any;

  /**
   * Outputs document deletion to parent component
   * @param {EventEmitter<any> = new EventEmitter(} ) documentDeleted [description]
   */
  @Output() documentDeleted: EventEmitter<any> = new EventEmitter();

  /**
   * Author search element ref
   * @type {ElementRef}
   */
  authorSearchInputElem: ElementRef;

  /**
   * Author search input element from view
   * @param {ElementRef} 'authorSearchInput') set authorSearchInputElemRef(authorSearchInputElem variable name
   */
  @ViewChild('authorSearchInput') set authorSearchInputElemRef(authorSearchInputElem: ElementRef) {
    this.authorSearchInputElem = authorSearchInputElem;
    if (this.authorSearchInputElem && this.authorSearchInputElem.nativeElement) this.authorSearchInputElem.nativeElement.value = '';
  }

  /**
   * Indicates if an author is being added
   * @type {boolean}
   */
  addingAuthor: boolean = false;

  /**
   * Indicates if a tag is being added
   * @type {boolean}
   */
  addingTags: boolean = false;

  /**
   * New author search term
   * @type {string}
   */
  addingAuthorSearchTerm: string;

  /**
   * Authored at date
   * @type {string}
   */
  authoredAt: string;

  /**
   * Contact info of author being viewed
   * @type {any}
   */
  authorBeingViewed: any;

  /**
   * List of all available authors
   * @type {Array<any>}
   */
  availableAuthors: Array<any> = [];

  /**
   * Indicates if document cloning is being processed
   * @type {boolean}
   */
  cloning: boolean = false;

  /**
   * Options for document clone
   * @type {any}
   */
  cloneOptions: any = {
    document_attachments: true,
    document_authors: true,
    document_bcc_contacts: true,
    document_cc_contacts: true,
    document_to_contacts: true,
  };

  /**
   * Document object with document info from show action
   * @type {any}
   */
  docShow: any;

  /**
   * URL for viewing document in iframe (includes auth token)
   * @type {string}
   */
  documentViewUrl: string;

  /**
   * Document type of the document
   * @type {any}
   */
  documentType: any;

  /**
   * Indicates if loading of document has erred
   *
   * @type  {boolean}
   */
  errorLoading: boolean = false;

  /**
   * Exhibit number of the document
   * @type {string}
   */
  exhibitNumber: string;

  /**
   * Used to mark sections of left side which are expanded
   * @type {any}
   */
  leftSectionExpand: any = { view_document: true };

  /**
   * Indicates if document info is loading
   * @type {boolean}
   */
  loading: boolean = true;

  /**
   * New name for the document
   * @type {string}
   */
  newName: string;

  /**
   * New tags to add to the document
   * @type {string}
   */
  newTags: string;

  /**
   * Document notes
   * @type {string}
   */
  notes: string;

  /**
   * Indicates if an author is being removed
   * @type {boolean}
   */
  removingAuthor: boolean = false;

  /**
   * Indicates if a tag is being removed
   * @type {boolean}
   */
  removingTag: boolean = false;

  /**
   * Rename request type for document
   * @type {string}
   */
  renamingDesire: string;

  /**
   * Indicates if document is being renamed
   * @type {boolean}
   */
  renamingDocument: boolean = false;

  /**
   * Indicates if document upload is being reset
   * @type {boolean}
   */
  resettingUpload: boolean = false;

  /**
   * Used to mark sections of right side which are expanded
   * @type {any}
   */
  rightSectionExpand: any = {};

  /**
   * Indicates if authored at is being saved
   * @type {boolean}
   */
  savingAuthoredAt: boolean = false;

  /**
   * Indicates if document type is being saved
   * @type {boolean}
   */
  savingDocumentType: boolean = false;

  /**
   * Indicates if exhibit number is being saved
   * @type {boolean}
   */
  savingExhibitNumber: boolean = false;

  /**
   * Indicates if document notes is being saved
   * @type {boolean}
   */
  savingNotes: boolean = false;

  /**
   * File uploader object from ng2 file upload
   * @type {FileUploader}
   */
  uploader: FileUploader;

  /**
   * Indicates if file is being uploaded
   * @type {boolean}
   */
  uploadingFile: boolean = false;

  /**
   * Indicates if contact info is being showed in authors list
   * @type {boolean}
   */
  viewAuthorMode: boolean = false;

  /**
   * Constructor
   * @param {DocumentService}      private documentService      Service to access documents controller
   * @param {DocumentStoreService} private documentStoreService Document store service to share document related objects between components in the same page
   * @param {MatterStoreService}   public  matterStoreService   Matter store service to share matter related objects between components in the same page
   * @param {ModalService}         private modalService         Modal service to display modals
   * @param {NotyService}          private notyService          Notification service for web UI
   * @param {TagService}           private tagService           Service to access tags controller
   */
  constructor(private documentService: DocumentService, private documentStoreService: DocumentStoreService, public matterStoreService: MatterStoreService, private modalService: ModalService, private notyService: NotyService, private tagService: TagService, private matterService: MatterService) {}

  // Component lifecycle functions

  /**
   * Called on component initialization
   */
  ngOnInit() {
    if(!this.doc){ return; }
    this.loadDocumentShow();
    this.documentService.getDocumentAuthors(this.doc.id).then((response_json) => {
      this.availableAuthors = response_json;
    });
  }

  // Component functions

  /**
   * Add tag
   */
  addTag() {
    if (!this.newTags) this.notyService.notify('Please enter new tags', 'info');
    this.addingTags = true;
    this.tagService
      .create({ angular: '1', document_id: this.docShow.id, tag: { tag: this.newTags } })
      .then((response_json) => {
        this.addingTags = false;
        this.notyService.notify('Tag saved', 'success');
        this.tagService.index({ document_id: this.docShow.id }).then((response_json) => (this.docShow.tag_list = response_json));
      })
      .catch(() => {
        this.addingTags = false;
        this.notyService.notify('Error adding tag', 'error');
      });
  }

  /**
   * Clone document
   */
  clone() {
    this.cloning = true;
    this.documentService
      .clone(this.doc.matter_id, this.doc.id, this.cloneOptions)
      .then((response_json) => {
        this.cloning = false;
        if (response_json.success) this.notyService.notify(response_json.text || 'Document cloned successfully', 'success');
        else this.notyService.notify(response_json.text || 'Error in cloning document', 'error');
      })
      .catch(() => {
        this.cloning = false;
        this.notyService.notify('Error in cloning document', 'error');
      });
  }

  /**
   * Close author contact info view
   */
  closeViewAuthor() {
    this.authorBeingViewed = null;
    this.viewAuthorMode = false;
  }

  /**
   * Delete document
   */
  deleteDocument() {
    this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-danger';
    this.modalService.confirmDialogComponent.open(`<p>Delete document - <strong class='text-danger'>${this.docShow.name}</strong></p>`).result.then(
      (result) => {
        this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
        if (result == 'confirmed') {
          this.docShow.deleting = true;
          this.documentService
            .deleteDocument(this.docShow.id)
            .then((response_json) => {
              this.docShow.deleting = false;
              this.documentDeleted.emit('Document deleted');
              this.notyService.notify(`Document deleted ${this.docShow.name}`, 'warning');
            })
            .catch(() => {
              this.docShow.deleting = false;
              this.notyService.notify('Error deleting document', 'error');
            });
        }
      },
      (reason) => {
        this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
      }
    );
  }

  /**
   * Delete tag
   * @param {string} tag   tag to delete
   * @param {number} index position of tag in list, used to remove the tag in the list in UI after tag has been deleted
   */
  deleteTag(tag: string, index: number) {
    this.removingTag = true;
    this.tagService
      .delete({ angular: '2', document_id: this.docShow.id, tag: tag })
      .then((response_json) => {
        this.removingTag = false;
        this.notyService.notify('Tag removed', 'info');
        this.docShow.tag_list = response_json;
      })
      .catch(() => {
        this.removingAuthor = false;
        this.notyService.notify('Error removing tag', 'error');
      });
  }

  /**
   * format version date from seconds - to be removed later once old matters show is removed and date is sent as formatted string by rails
   * @param  {number} seconds Seconds to format date from
   * @return {string}         Formatted date string
   */
  formatVersionDate(seconds: number): string {
    var milliseconds = seconds * 1000;
    var date = new Date(Number(milliseconds));
    var date_str = '';
    var tmp = date.getMonth() + 1;
    date_str += tmp > 9 ? tmp : '0' + tmp;
    tmp = date.getDate();
    date_str += '-' + (tmp > 9 ? tmp : '0' + tmp);
    date_str += '-' + date.getFullYear();
    tmp = date.getHours() % 12;
    date_str += ' ' + (tmp == 0 ? '12' : tmp > 9 ? tmp : '0' + tmp);
    tmp = date.getMinutes() + 1;
    date_str += ':' + (tmp > 9 ? tmp : '0' + tmp);
    tmp = date.getSeconds() + 1;
    date_str += ':' + (tmp > 9 ? tmp : '0' + tmp);
    date_str += ' ' + (date.getHours() > 10 ? 'pm' : 'am');
    return date_str;
  }

  /**
   * Initialize uploader
   */
  initializeUploader() {
    this.uploader = new FileUploader({ itemAlias: 'uploaded_file', url: `/documents/${this.docShow.id}.json` });
    let scope = this;
    this.uploader.onAfterAddingFile = (fileItem) => {
      if (this.uploader.queue.length > 1) {
        let uploadQueue: any = this.uploader.queue;
        let len: number = uploadQueue.length;
        uploadQueue.splice(0, len - 1);
      }
    };
    this.uploader.onBuildItemForm = (item, form) => {
      form.append('upload', true);
    };
    this.uploader.onSuccessItem = (item: any, response: string, status: number, headers: any) => {
      scope.uploadingFile = false;
      let response_json: any = JSON.parse(response);
      if (response_json.success) {
        this.notyService.notify('Document uploaded', 'success');
        this.documentStoreService.matterDocumentsListComponent!.loadDocuments();
        this.resettingUpload = true;
        let scope: DocumentShowComponent = this;
        window.setTimeout(() => {
          scope.uploader.queue = [];
          scope.resettingUpload = false;
        }, 500);
      } else this.notyService.notify(response_json.text, 'error');
    };
    this.uploader.onErrorItem = (item: any, response: string, status: number, headers: any) => {
      this.notyService.notify('Error uploading document', 'error');
    };
  }

  /**
   * Build the document view URL with authentication token
   */
  buildDocumentViewUrl(): void {
    const token = window.localStorage.getItem('token');
    if (token && this.docShow) {
      this.documentViewUrl = `/documents/${this.docShow.id}/download?disposition=inline&ts=${this.docShow.ts}&token=${encodeURIComponent(token)}`;
    }
  }

  buildDocumentDownloadUrl(): string {
    const token = window.localStorage.getItem('token');
    const baseUrl = `/documents/${this.docShow.id}/download?disposition=attachment`;

    return token ? `${baseUrl}&token=${encodeURIComponent(token)}` : baseUrl;
  }

  downloadDocument(event: Event): boolean {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    window.location.href = this.buildDocumentDownloadUrl();

    return false;
  }

  /**
   * Load document show info
   */
  loadDocumentShow() {
    this.loading = true;
    if(!this.doc){ return; }
    this.documentService
      .getDocumentShow(this.doc.id)
      .then((response_json) => {
        if (!response_json) {
          this.errorLoading = true;
          this.notyService.notify(`Error loading document ${this.doc ? this.doc.name : ''}`, 'error');
          return;
        }
        if (response_json) {
          this.docShow = response_json;
          this.buildDocumentViewUrl();
          for (let version of this.docShow.versions) version.created_at = this.formatVersionDate(version.created_at);
          this.authoredAt = this.docShow.authored_at_str;
          this.documentType = this.docShow.document_type;
          this.documentType.label = this.documentType.name;
          this.documentType.value = this.documentType.id;
          this.notes = this.docShow.notes;
          this.exhibitNumber = this.docShow.exhibit_id;
          this.loading = false;
          this.resettingUpload = true;
          window.setTimeout(() => {
            this.initializeUploader();
            this.resettingUpload = false;
          }, 500);
        }
      })
      .catch();
  }

  /**
   * Called on new author select
   * @param {any} document_author Selected document author to add
   */
  onAuthorSelect(document_author: any) {
    for (let author of this.docShow.document_authors)
      if (author.contact_id == document_author.id) {
        this.notyService.notify('Author already added', 'warning');
        return;
      }
    this.addingAuthor = true;
    this.documentService
      .addAuthor(document_author, this.docShow.id)
      .then((response_json) => {
        this.addingAuthor = false;
        this.docShow.document_authors = response_json;
        this.notyService.notify('Author added', 'success');
        this.addingAuthorSearchTerm = '';
        if (this.authorSearchInputElem) this.authorSearchInputElem.nativeElement.value = '';
      })
      .catch(() => {
        this.addingAuthor = false;
        this.notyService.notify('Error adding author', 'error');
        if (this.authorSearchInputElem) this.authorSearchInputElem.nativeElement.value = '';
      });
  }

  reloadMatterDocumentsList() {
    if (this.documentStoreService.matterDocumentsListComponent) this.documentStoreService.matterDocumentsListComponent.loadDocuments();
  }

  /**
   * Remove author
   * @param {any}    document_author Author to remove
   * @param {number} index           position of author in the list in UI
   */
  removeAuthor(document_author: any, index: number) {
    this.removingAuthor = true;
    this.documentService
      .destroyAuthor(document_author.id)
      .then((response_json) => {
        this.removingAuthor = false;
        this.notyService.notify('Author removed', 'info');
        this.docShow.document_authors.splice(index, 1);
      })
      .catch(() => {
        this.removingAuthor = false;
        this.notyService.notify('Error removing author', 'error');
      });
  }

  /**
   * Save a new name for the document
   * @param {string} desire Rename request type
   */
  saveApprovedName(desire: string) {
    this.renamingDocument = true;
    this.documentService
      .rename(this.docShow.id, desire, this.newName)
      .then((response_json) => {
        this.renamingDocument = false;
        if (response_json.error) {
          this.notyService.notify(response_json.error, 'error');
          return;
        }
        this.newName = '';
        this.docShow.name = response_json.new_doc_name;
        this.notyService.notify('Document rename successful', 'success');
        this.reloadMatterDocumentsList();
      })
      .catch(() => {
        this.renamingDocument = false;
        this.notyService.notify('Error renaming document', 'error');
      });
  }

  /**
   * SAve new document name after processing rename desire
   * @param {string} desire Rename request type
   */
  saveName(desire: string) {
    this.renamingDesire = desire;
    let original_extension = this.documentService.extension(this.docShow.name);
    let new_extension = this.documentService.extension(this.newName);
    if (desire == 'restore_name') this.saveApprovedName(desire);
    else if (new_extension == '' && desire != 'restore_name') {
      this.modalService.alertDialogComponent.open(`<p>Your new file name must include an extension like:  ${this.newName}.${original_extension}</p>`);
      this.newName = `${this.newName}.${original_extension}`;
    } else if (original_extension != new_extension) {
      this.modalService.confirmDialogComponent.open(`<p>You are changing the extension of the file from ${original_extension} to '${new_extension} and this may affect your ability to view this file in the future.  Do you want to continue?</p>`).result.then(
        (result) => {
          if (result == 'confirmed') this.saveApprovedName(desire);
        },
        (reason) => {}
      );
    } else this.saveApprovedName(desire);
  }

  /**
   * Save notes
   */
  saveNotes() {
    this.savingNotes = true;
    this.documentService
      .saveNotes(this.notes, this.docShow.id)
      .then((response_json) => {
        this.savingNotes = false;
        this.docShow.notes = this.notes;
        this.notyService.notify('Notes saved', 'success');
        this.reloadMatterDocumentsList();
      })
      .catch(() => {
        this.savingNotes = false;
        this.notyService.notify('Error in saving notes', 'error');
      });
  }

  /**
   * Set a new authored at date
   */
  setAuthoredAt() {
    if (!this.authoredAt) this.notyService.notify('Please fill in authored at date', 'info');
    this.savingAuthoredAt = true;
    this.documentService
      .setAuthoredAt(this.authoredAt, this.docShow.id)
      .then((response_json) => {
        this.savingAuthoredAt = false;
        this.docShow.authored_at_str = this.authoredAt;
        this.notyService.notify('Authored at saved', 'success');
        this.reloadMatterDocumentsList();
      })
      .catch(() => {
        this.savingAuthoredAt = false;
        this.notyService.notify('Error saving authored at', 'error');
      });
  }

  /**
   * Set a new document type
   */
  setDocumentType() {
    if (!this.documentType) {
      this.notyService.notify('Please select a document type', 'info');
      return;
    }
    this.savingDocumentType = true;
    this.documentService
      .setDocumentType(this.documentType, this.docShow.id)
      .then((response_json) => {
        this.savingDocumentType = false;
        this.documentType.name = this.documentType.label;
        this.docShow.document_type = this.documentType;
        this.notyService.notify('Document type updated', 'success');
        this.reloadMatterDocumentsList();
      })
      .catch(() => {
        this.savingDocumentType = false;
        this.notyService.notify('Error saving document type', 'error');
      });
  }

  /**
   * Change document version
   * @param {any} version Version to switch to
   */
  setDocumentVersion(version: any) {
    this.docShow.reverting = true;
    this.documentService
      .setDocumentVersion(version.version, this.docShow.id)
      .then((response_json) => {
        this.docShow.reverting = false;
        this.loadDocumentShow();
        this.notyService.notify(response_json.text || `Document reverted to version ${version.version}`, 'success');
        this.reloadMatterDocumentsList();
      })
      .catch(() => {
        this.docShow.reverting = false;
        this.notyService.notify(`Error reverting to version ${version.version}`, 'error');
      });
  }

  /**
   * Set editability of document
   */
  setEditability() {
    this.docShow.settingEditability = true;
    this.documentService
      .setEditability(!this.docShow.is_editable, this.docShow.id)
      .then((response_json) => {
        this.docShow.settingEditability = false;
        this.docShow.is_editable = response_json.is_editable;
        this.reloadMatterDocumentsList();
      })
      .catch(() => {
        this.docShow.settingEditability = false;
      });
  }

  /**
   * Set exhibit number
   */
  setExhibitNumber() {
    if (!this.exhibitNumber) {
      this.notyService.notify('Please enter an exhibit number', 'info');
      return;
    }
    this.savingExhibitNumber = true;
    this.documentService
      .setExhibitNumber(this.exhibitNumber, this.docShow.id)
      .then((response_json) => {
        this.savingExhibitNumber = false;
        this.docShow.exhibit_id = response_json.exhibit_id;
        this.notyService.notify('Exhibit number set', 'success');
        this.reloadMatterDocumentsList();
      })
      .catch(() => {
        this.savingExhibitNumber = false;
        this.notyService.notify('Error setting exhibit number', 'error');
      });
  }

  /**
   * Called to upload document
   */
  uploadDocument() {
    if (this.uploader.queue[0]) {
      this.uploadingFile = true;
      this.uploader.queue[0].method = 'PUT';
      this.uploader.queue[0].upload();
      this.reloadMatterDocumentsList();
    } else this.notyService.notify('File not selected for upload', 'error');
  }

  /**
   * View author contact info
   * @param {any} document_author Author to view the contact info for
   */
  viewAuthor(document_author: any) {
    this.authorBeingViewed = document_author;
    this.viewAuthorMode = true;
  }
}
